import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, HandHeart, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { evaluatePassword, isValidEmail } from "../lib/passwordStrength";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import Logo from "../components/Logo";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "donor" });
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "email" && emailError) setEmailError("");
  };

  const { allPassed: strongEnough } = evaluatePassword(form.password);
  const emailLooksValid = isValidEmail(form.email);
  const canSubmit = form.name.trim().length >= 2 && emailLooksValid && strongEnough && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!emailLooksValid) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (!strongEnough) {
      toast.error("Please meet all the password requirements below.");
      return;
    }
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome to Dkarez, ${user.name.split(" ")[0]}`);
      navigate(user.role === "requester" ? "/create" : "/campaigns");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="w-full max-w-md">
        <div className="flex justify-center mb-7"><Logo size="lg" /></div>
        <div className="premium-card p-8 sm:p-10">
          <h1 className="font-serif-display text-3xl text-brand-text">Join Dkarez</h1>
          <p className="text-brand-muted mt-1.5">Build hope, one story at a time.</p>

          <div className="mt-7">
            <GoogleSignInButton label="Sign up with Google" />
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-brand-border" />
            <span className="text-xs uppercase tracking-widest text-brand-muted">or with email</span>
            <div className="h-px flex-1 bg-brand-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-2">I want to</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setField("role", "donor")}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${form.role === "donor" ? "border-brand-primary bg-brand-primary-light" : "border-brand bg-brand-bg hover:border-brand-primary/40"}`}
                  data-testid="register-role-donor">
                  <Heart size={18} className="text-brand-primary mb-2" strokeWidth={1.6} />
                  <p className="font-medium text-brand-text">Donate</p>
                  <p className="text-xs text-brand-muted mt-1">Support causes</p>
                </button>
                <button type="button" onClick={() => setField("role", "requester")}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${form.role === "requester" ? "border-brand-primary bg-brand-primary-light" : "border-brand bg-brand-bg hover:border-brand-primary/40"}`}
                  data-testid="register-role-requester">
                  <HandHeart size={18} className="text-brand-primary mb-2" strokeWidth={1.6} />
                  <p className="font-medium text-brand-text">Request help</p>
                  <p className="text-xs text-brand-muted mt-1">Raise support</p>
                </button>
              </div>
            </div>

            <div>
              <label className="eyebrow block mb-2">Full name</label>
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.6} />
                <input type="text" value={form.name} onChange={(e) => setField("name", e.target.value)}
                       className="input-premium !pl-11" placeholder="Jane Doe" required minLength={2}
                       data-testid="register-name-input" />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.6} />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onBlur={() => {
                    if (form.email && !isValidEmail(form.email)) {
                      setEmailError("Please enter a valid email address.");
                    }
                  }}
                  className={`input-premium !pl-11 ${emailError ? "!border-red-300" : ""}`}
                  placeholder="you@example.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
              {emailError && (
                <p className="mt-2 text-xs text-red-600" data-testid="register-email-error">{emailError}</p>
              )}
            </div>
            <div>
              <label className="eyebrow block mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.6} />
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  className="input-premium !pl-11"
                  placeholder="At least 10 characters with letter, number & symbol"
                  required
                  data-testid="register-password-input"
                />
              </div>
              <PasswordStrengthMeter password={form.password} />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-gold w-full !py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account…" : "Create account"}
              <ArrowRight size={16} strokeWidth={1.8} className="ml-2" />
            </button>
          </form>

          <p className="text-center text-sm text-brand-muted mt-6">
            Already have one? <Link to="/login" className="text-brand-primary font-medium hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
